/**
 * useSchedulePlan - Hook for scheduling meal plans to the user's calendar
 *
 * This hook handles the complete workflow for scheduling a meal plan:
 * - Date selection via modal
 * - Conflict detection with existing meals/groceries
 * - User confirmations for overwrites
 * - Virtual plan saving if needed
 * - Meal creation and calendar scheduling
 * - Working state with "Crunching the numbers..." message
 */

import { useState, useCallback } from 'react';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { createMealsForPlan } from '@utils/Meals';
import { createNewDocument } from '@utils/Content';
import { deleteVirtualPlan } from '@utils/Plans';
import { isMealInGroceries } from '@utils/Grocery';
import MealStore from '@stores/MealStore';
import MealActions from '@actions/MealActions';
import confirm from '@utils/Confirm';
import PlanActions from '@actions/PlanActions';
import GroceryStore from '@stores/GroceryStore';
import GroceryActions from '@actions/GroceryActions';
import Analytics from '@utils/Analytics';

/**
 * Hook for scheduling meal plans to calendar
 *
 * @param {Object} config - Configuration object
 * @param {Object} config.plan - The meal plan to schedule
 * @param {Object} config.profile - User profile with family data
 * @param {Object} config.user - Current user
 * @param {Function} config.onComplete - Callback when scheduling completes
 * @param {Object} config.router - Router instance for navigation
 * @param {Object} config.modalManager - Modal manager for date picker
 * @param {Function} config.showUpgradeForm - Function to show upgrade/subscription form
 * @param {Function} config.showLoginForm - Function to show login form
 * @param {boolean} config.isCordova - Whether running in Cordova app
 * @param {boolean} config.synced - Whether plan is synced
 * @param {Object} config.recipes - Recipe data for premium content detection
 * @returns {Object} Schedule plan interface
 */
export const useSchedulePlan = (config = {}) => {
    const {
        plan,
        profile,
        user,
        onComplete,
        router,
        modalManager,
        showUpgradeForm,
        showLoginForm,
        isCordova = false,
        synced = true,
        recipes = {},
    } = config;

    // State
    const [isScheduling, setIsScheduling] = useState(false);
    const [isInSchedulingFlow, setIsInSchedulingFlow] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [error, setError] = useState(null);

    /**
     * Check if scheduling would overwrite existing meals/groceries
     */
    const checkConflicts = useCallback((planToCheck, dateStart) => {
        if (!dateStart) dateStart = moment();

        // Calculate plan duration from actual meal items (source of truth)
        const maxOffset =
            planToCheck.items && planToCheck.items.length > 0
                ? Math.max(...planToCheck.items.map((item) => item.offset || 0))
                : 0;
        const planDuration = maxOffset + 1; // +1 because offset is 0-based

        const dateEnd = moment(dateStart).add(planDuration, 'day');

        // Find existing meals in date range
        const overwrittenMeals = MealStore.getMeals().filter(
            (m) => dateStart.isSameOrBefore(m.date, 'day') && dateEnd.isSameOrAfter(m.date, 'day') && !m.deleted
        );

        let wouldOverwriteMeals = overwrittenMeals.length > 0;
        let wouldOverwriteGroceries = false;
        let wouldOverwritePurchasedGroceries = false;

        // Check grocery conflicts
        const groceries = GroceryStore.getGroceries();
        for (let i in overwrittenMeals) {
            const meal = overwrittenMeals[i];
            const isInGroceries = isMealInGroceries(meal, groceries);
            const areGroceriesPurchased = isInGroceries && !isMealInGroceries(meal, groceries, true);

            if (isInGroceries) {
                wouldOverwriteGroceries = true;
            }

            if (areGroceriesPurchased) {
                wouldOverwritePurchasedGroceries = true;
                break; // One is enough to trigger this warning
            }
        }

        return {
            wouldOverwriteMeals,
            wouldOverwriteGroceries,
            wouldOverwritePurchasedGroceries,
            overwrittenMeals,
        };
    }, []);

    /**
     * Create plan document if it's virtual or dirty
     */
    const createPlanIfNeeded = useCallback(async (planToSave, isDirty) => {
        // If plan exists and isn't dirty, return as-is
        if (planToSave.links && planToSave.links.self && !isDirty) {
            return planToSave;
        }

        // Create new document in database
        const { uuid, links, created, merchant, type, status, ...rest } = planToSave;

        try {
            const response = await createNewDocument('plan', rest, 'plan/1');

            // Clean up virtual plan
            if (planToSave.uuid) {
                deleteVirtualPlan(planToSave.uuid, response.uuid);
            }

            return response;
        } catch (error) {
            throw new Error(`Failed to save plan: ${error.message}`);
        }
    }, []);

    /**
     * Perform the actual scheduling - create meals and add to calendar
     */
    const performScheduling = useCallback(
        async (planToSchedule, dateStart, clearGroceries = false, keepExisting = false) => {
            setIsScheduling(true);
            setError(null);

            const updatedProfileKey = {
                ...profile.profile_key,
                meal_types: planToSchedule.meal_types || profile.preferences?.meal_types || [],
            };

            // Base plan marker — used directly for Replace, used as template for Keep
            const basePlanMarker = {
                uuid: uuid(),
                plan_uuid: planToSchedule.uuid,
                date_start: dateStart.format('YYYY-MM-DD'),
                created: moment().format(),
                profile_key: updatedProfileKey,
            };

            // Generate all meals for the full plan range
            const { meals, maxDate } = createMealsForPlan(
                planToSchedule,
                dateStart,
                user,
                basePlanMarker
            );

            basePlanMarker.date_end = maxDate.format('YYYY-MM-DD');

            if (clearGroceries) {
                GroceryActions.mealFeedRegen();
            }

            if (keepExisting) {
                const allMeals = MealStore.getMeals();

                // A day is occupied if it has ANY existing meal — regardless of meal type
                const occupiedDays = new Set(
                    allMeals
                        .filter(
                            (m) =>
                                moment(m.date).isBetween(dateStart, maxDate, 'day', '[]') &&
                                !m.deleted
                        )
                        .map((m) => moment(m.date).format('YYYY-MM-DD'))
                );

                // All dates the plan would cover, sorted (plan day 1, 2, 3 ...)
                const uniquePlanDates = [...new Set(meals.map((m) => m.date))].sort();

                // Empty days within the plan range, sorted
                const emptyDays = uniquePlanDates.filter((d) => !occupiedDays.has(d));

                if (emptyDays.length === 0) {
                    setIsScheduling(false);
                    setIsInSchedulingFlow(false);
                    await confirm(
                        <div>
                            <p>All days in this date range already have meals scheduled. There are no empty days to fill from this plan.</p>
                        </div>,
                        { acceptText: 'OK', rejectText: null }
                    );
                    return;
                }

                // Map plan day 1 → first empty day, plan day 2 → second empty day, etc.
                const dateRemap = {};
                emptyDays.forEach((emptyDay, i) => {
                    dateRemap[uniquePlanDates[i]] = emptyDay;
                });

                // Remap meal dates and drop meals beyond available empty days
                const mealsForEmptyDays = meals
                    .filter((m) => dateRemap[m.date] !== undefined)
                    .map((m) => ({ ...m, date: dateRemap[m.date] }));

                // Group target empty days into consecutive segments — each gets its own plan marker
                const segments = [];
                let currentSegment = [emptyDays[0]];
                for (let i = 1; i < emptyDays.length; i++) {
                    if (moment(emptyDays[i]).diff(moment(emptyDays[i - 1]), 'days') === 1) {
                        currentSegment.push(emptyDays[i]);
                    } else {
                        segments.push(currentSegment);
                        currentSegment = [emptyDays[i]];
                    }
                }
                segments.push(currentSegment);

                // One plan marker per consecutive segment
                const planMarkers = segments.map((segment) => ({
                    uuid: uuid(),
                    plan_uuid: planToSchedule.uuid,
                    date_start: segment[0],
                    date_end: segment[segment.length - 1],
                    created: moment().format(),
                    profile_key: updatedProfileKey,
                }));

                // Assign each meal to the plan marker whose date range it falls within
                const mealsWithMarkers = mealsForEmptyDays.map((meal) => {
                    const marker = planMarkers.find(
                        (m) =>
                            moment(meal.date).isSameOrAfter(m.date_start, 'day') &&
                            moment(meal.date).isSameOrBefore(m.date_end, 'day')
                    );
                    return { ...meal, plan_uuid: marker.uuid };
                });

                PlanActions.upsertPlans(planMarkers);
                MealActions.upsertMeals(mealsWithMarkers);
            } else {
                // Replace: clear the date range and schedule all new meals.
                //
                // Every meal in range goes, not only the meal types the new plan happens to
                // carry. A plan is scheduled as it stands, whatever meal types the profile
                // has, so after replacing it should be the only thing on those days. Keeping
                // a meal from a dropped meal type leaves it counted in the daily totals but
                // absent from the page, which the member can neither see nor remove.
                //
                // Notes are excluded by the meal_type list: weight, water, journal entries
                // and meal photos are the member's own records, not plan meals.
                const allMeals = MealStore.getMeals();
                const overwritten = allMeals.filter(
                    (m) =>
                        moment(m.date).isBetween(dateStart, maxDate, 'day', '[]') &&
                        ['fresh', 'leftover', 'food'].includes(m.meal_type) &&
                        !m.deleted
                );

                // A leftover can fall past the new plan's last day while the meal it came
                // from sits inside the range. Take those with the parent, so no leftover
                // outlives its own meal.
                const overwrittenUuids = new Set(overwritten.map((m) => m.uuid));
                const strandedLeftovers = allMeals.filter(
                    (m) =>
                        !m.deleted &&
                        m.parent_uuid &&
                        overwrittenUuids.has(m.parent_uuid) &&
                        !overwrittenUuids.has(m.uuid)
                );

                MealActions.deleteMeals(overwritten.concat(strandedLeftovers));
                PlanActions.upsertPlans([basePlanMarker]);
                MealActions.upsertMeals(meals);
            }

            Analytics.scheduleMealPlan({
                'Meal Plan UUID': planToSchedule.uuid,
                'Meal Plan Name': planToSchedule.title,
                'Plan Start Date': dateStart.toISOString(),
            });

            if (!router || !router.push) {
                throw new Error('Router is not available for navigation');
            }

            router.push({
                pathname: '/meals',
                query: { date: dateStart.format('YYYY-MM-DD') },
            });

            if (onComplete) {
                onComplete(basePlanMarker);
            }

            setIsScheduling(false);
            setIsInSchedulingFlow(false);
        },
        [user, profile, onComplete, router]
    );

    /**
     * Handle conflict resolution and proceed with scheduling
     */
    const handleConflictsAndSchedule = useCallback(
        async (planToSchedule, dateStart, isDirty = false) => {

            // Save plan if needed
            const savedPlan = await createPlanIfNeeded(planToSchedule, isDirty);

            // Check for conflicts
            const conflicts = checkConflicts(savedPlan, dateStart);

            const proceedWithScheduling = (clearGroceries = false, keepExisting = false) => {
                performScheduling(savedPlan, dateStart, clearGroceries, keepExisting);
            };

            const cancelSchedulingFlow = () => {
                setSelectedDate(null);
                setIsInSchedulingFlow(false);
            };

            // Handle purchased groceries conflict
            if (conflicts.wouldOverwritePurchasedGroceries) {
                const result = await confirm(
                    <div>
                        <p>You've already planned meals for some of these days. Do you want to replace them with the new plan, or keep what you have?</p>
                    </div>,
                    {
                        acceptText: 'Keep Existing Meals',
                        rejectText: 'Replace',
                    }
                );

                if (result === true) {
                    proceedWithScheduling(false, true);
                } else if (result === false) {
                    proceedWithScheduling(true, false);
                } else {
                    cancelSchedulingFlow();
                }
                return;
            }

            // Handle grocery list conflict
            if (conflicts.wouldOverwriteGroceries) {
                const result = await confirm(
                    <div>
                        <p>You've already planned meals for some of these days. Do you want to replace them with the new plan, or keep what you have?</p>
                    </div>,
                    {
                        acceptText: 'Keep Existing Meals',
                        rejectText: 'Replace',
                    }
                );

                if (result === true) {
                    proceedWithScheduling(false, true);
                } else if (result === false) {
                    proceedWithScheduling(true, false);
                } else {
                    cancelSchedulingFlow();
                }
                return;
            }

            // Handle meal conflicts
            if (conflicts.wouldOverwriteMeals) {
                const result = await confirm(
                    <div>
                        <p>You've already planned meals for some of these days. Do you want to replace them with the new plan, or keep what you have?</p>
                    </div>,
                    {
                        acceptText: 'Keep Existing Meals',
                        rejectText: 'Replace',
                    }
                );

                if (result === true) {
                    proceedWithScheduling(false, true);
                } else if (result === false) {
                    proceedWithScheduling(true, false);
                } else {
                    cancelSchedulingFlow();
                }
                return;
            }

            // No conflicts, proceed directly
            proceedWithScheduling(false);
        },
        [createPlanIfNeeded, checkConflicts, performScheduling, confirm]
    );

    /**
     * Check if user is premium content and needs upgrade
     */
    const isPremiumContent = useCallback(() => {
        // Determine if the plan or any recipe in it is protected
        let isPremiumContent = plan.protection != 'public';

        if (plan.items && recipes) {
            plan.items.forEach((meal) => {
                if (meal.deleted) {
                    return;
                }

                const recipe = recipes[meal.recipe_uuid];

                if (!recipe) {
                    isPremiumContent = true;
                    return;
                }

                if (recipe.protection !== 'public') {
                    isPremiumContent = true;
                }
            });
        }

        return isPremiumContent;
    }, [plan, recipes]);

    /**
     * Check authentication and subscription status
     */
    const checkAuthAndSubscription = useCallback(() => {
        // Check if user is logged in
        if (!user || !user.uuid) {
            if (showLoginForm) {
                showLoginForm();
            }
            return false;
        }

        // Check subscription/capability for meal planner
        if (!user.capabilities?.meal_planner && isPremiumContent()) {
            if (showUpgradeForm) {
                showUpgradeForm({feature: 'meal_planner'});
            }
            return false;
        }

        return true;
    }, [user, showLoginForm, showUpgradeForm, isPremiumContent]);

    /**
     * Start the scheduling process
     */
    const startScheduling = useCallback(
        (isDirty = false) => {
            if (isScheduling || isInSchedulingFlow) {
                return; // Already in progress
            }

            // Validate authentication and subscription
            if (!checkAuthAndSubscription()) {
                return;
            }

            // Set scheduling flow state to prevent multiple calls
            setIsInSchedulingFlow(true);

            // If date is already selected, proceed with scheduling
            if (selectedDate) {
                handleConflictsAndSchedule(plan, selectedDate, isDirty);
            } else {
                // Show date picker modal
                modalManager.showPickDate({
                    onPickDate: (date) => onDateSelected(date, isDirty),
                    onClose: () => cancelScheduling(),
                });
            }
        },
        [
            plan,
            selectedDate,
            isScheduling,
            isInSchedulingFlow,
            handleConflictsAndSchedule,
            checkAuthAndSubscription,
            modalManager,
        ]
    );

    /**
     * Handle date selection from picker
     */
    const onDateSelected = useCallback(
        (date, isDirty = false) => {
            setSelectedDate(date);
            modalManager.closePickDate();
            handleConflictsAndSchedule(plan, date, isDirty);
        },
        [plan, handleConflictsAndSchedule, modalManager]
    );

    /**
     * Cancel scheduling process
     */
    const cancelScheduling = useCallback(() => {
        setSelectedDate(null);
        setIsScheduling(false);
        setIsInSchedulingFlow(false);
        setError(null);
        modalManager.closePickDate();
    }, [modalManager]);

    return {
        // State
        isScheduling,
        selectedDate,
        error,

        // Actions
        startScheduling,
        onDateSelected,
        cancelScheduling,

        // Utilities
        checkConflicts,

        // Status
        canSchedule: !!(plan && !isScheduling && !isInSchedulingFlow && synced),
        isWorking: isScheduling || isInSchedulingFlow,
    };
};

export default useSchedulePlan;
